import { useEffect, useMemo, useState } from 'react';
import Modal from '@/components/ui/Modal';
import * as adminService from '@/services/adminService';
import { resolveAssetUrl } from '@/utils/assets';

const rarities = [
  { value: 'common', label: 'Comum' },
  { value: 'rare', label: 'Rara' },
  { value: 'epic', label: 'Épica' },
  { value: 'legendary', label: 'Lendária' },
];

const requirementActions = [
  { value: 'pois_visited', label: 'POIs visitados' },
  { value: 'coins_collected', label: 'Moedas recolhidas' },
  { value: 'missions_completed', label: 'Missões completas' },
  { value: 'redemptions', label: 'Resgates' },
  { value: 'zone_conquered', label: 'Zona conquistada' },
];

const emptyForm = {
  name: '',
  description: '',
  category: 'exploration',
  requirementAction: 'pois_visited',
  requirementTarget: 1,
  rawRequirement: null,
  xpReward: 100,
  coinReward: 50,
  rarity: 'common',
  imageUrl: '',
  isActive: true,
};

const fieldClass = 'w-full rounded-lg border border-fg/10 bg-black/25 px-3 py-2 text-sm text-fg outline-none focus:border-accent';
const labelClass = 'text-xs font-bold uppercase tracking-[0.16em] text-fg/40';

const rarityStyles = {
  common: 'border-fg/15 bg-fg/5 text-fg/60',
  rare: 'border-ocean-light/30 bg-ocean-light/10 text-ocean-light',
  epic: 'border-gem/30 bg-gem/10 text-gem',
  legendary: 'border-accent/40 bg-accent/15 text-accent',
};

const getRequirementLabel = (requirement) => {
  const type = requirement?.type || requirement?.action;
  if (!type) return 'Sem requisito';
  const action = requirementActions.find((item) => item.value === type)?.label || type;
  return `${action} - Meta ${requirement.threshold ?? requirement.target ?? 1}`;
};

const getImageSrc = (imageUrl) => resolveAssetUrl(imageUrl);

export default function AdminBadgesPage() {
  const [badges, setBadges] = useState([]);
  const [filter, setFilter] = useState('all');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const stats = useMemo(() => ({
    total: badges.length,
    active: badges.filter((badge) => badge.isActive).length,
    rarePlus: badges.filter((badge) => ['rare', 'epic', 'legendary'].includes(badge.rarity)).length,
    rewards: badges.reduce((sum, badge) => sum + Number(badge.coinReward || 0), 0),
  }), [badges]);

  const filteredBadges = useMemo(() => {
    if (filter === 'all') return badges;
    return badges.filter((badge) => badge.rarity === filter);
  }, [badges, filter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await adminService.badges.list({ limit: 100 });
      setBadges(res.data.data || []);
      setError('');
    } catch {
      setError('Não foi possível carregar as medalhas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError('');
    setNotice('');
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const editBadge = (badge) => {
    const requirement = badge.requirement || {};
    const requirementType = requirement.type || requirement.action || 'pois_visited';
    setEditingId(badge.id);
    setForm({
      name: badge.name || '',
      description: badge.description || '',
      category: badge.category || 'exploration',
      requirementAction: requirementType,
      requirementTarget: requirement.threshold ?? requirement.target ?? 1,
      rawRequirement: requirement,
      xpReward: badge.xpReward ?? 0,
      coinReward: badge.coinReward ?? 0,
      rarity: badge.rarity || 'common',
      imageUrl: badge.imageUrl || '',
      isActive: Boolean(badge.isActive),
    });
    setError('');
    setNotice('');
    setModalOpen(true);
  };

  const buildRequirement = () => {
    const target = Number(form.requirementTarget) || 1;
    if (form.rawRequirement?.type === 'zone_conquered' || form.requirementAction === 'zone_conquered') {
      return {
        ...form.rawRequirement,
        type: 'zone_conquered',
        threshold: target,
      };
    }
    return {
      type: form.requirementAction,
      threshold: target,
    };
  };

  const buildPayload = () => ({
    name: form.name,
    description: form.description,
    category: form.category || 'exploration',
    requirement: buildRequirement(),
    xpReward: Number(form.xpReward) || 0,
    coinReward: Number(form.coinReward) || 0,
    rarity: form.rarity,
    imageUrl: form.imageUrl || null,
    isActive: form.isActive,
  });

  const generateImage = async () => {
    setGeneratingImage(true);
    setError('');
    setNotice('');

    try {
      const payload = buildPayload();
      const res = await adminService.generateBadgeImage({
        name: payload.name,
        description: payload.description,
        category: payload.category,
        rarity: payload.rarity,
        requirement: payload.requirement,
      });
      setForm((current) => ({ ...current, imageUrl: res.data.data.imageUrl }));
      setNotice('Imagem da medalha gerada com sucesso.');
    } catch (err) {
      setError(err.response?.data?.message || 'Nao foi possivel gerar a imagem da medalha.');
    } finally {
      setGeneratingImage(false);
    }
  };

  const saveBadge = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setNotice('');

    try {
      if (editingId) {
        await adminService.badges.update(editingId, buildPayload());
        setNotice('Medalha atualizada com sucesso.');
      } else {
        await adminService.badges.create(buildPayload());
        setNotice('Medalha criada com sucesso.');
      }
      await loadData();
      setModalOpen(false);
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível guardar a medalha.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (badge) => {
    try {
      await adminService.badges.update(badge.id, { isActive: !badge.isActive });
      await loadData();
    } catch {
      setError('Não foi possível alterar o estado da medalha.');
    }
  };

  const removeBadge = async (badge) => {
    if (!window.confirm(`Remover a medalha "${badge.name}"?`)) return;
    try {
      await adminService.badges.remove(badge.id);
      await loadData();
    } catch {
      setError('Não foi possível remover a medalha.');
    }
  };

  return (
    <section className="min-h-full">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-accent/70">Progressão</p>
          <h1 className="mt-1 font-serif text-3xl font-black text-accent">Gestão de Medalhas</h1>
          <p className="mt-2 max-w-2xl text-sm text-fg/55">
            Configura conquistas, requisitos e recompensas que reconhecem a exploração dos jogadores.
          </p>
        </div>
        <button type="button" onClick={openCreateModal} className="rounded-lg border border-accent/40 bg-accent/10 px-4 py-2 text-sm font-bold text-accent hover:bg-accent/20">
          Nova medalha
        </button>
      </div>

      {(error || notice) && (
        <div className={`mt-5 rounded-lg border p-4 text-sm font-bold ${error ? 'border-energy/40 bg-energy/10 text-energy' : 'border-forest-light/40 bg-forest-light/10 text-forest-light'}`}>
          {error || notice}
        </div>
      )}

      <div className="mt-6 grid gap-3 md:grid-cols-4">
        {[
          ['Total', stats.total],
          ['Ativas', stats.active],
          ['Raras+', stats.rarePlus],
          ['Moedas', stats.rewards],
        ].map(([label, value]) => (
          <div key={label} className="rounded-lg border border-fg/10 bg-fg/[0.04] p-4">
            <div className="text-xs font-bold uppercase tracking-[0.16em] text-fg/35">{label}</div>
            <div className="mt-2 font-serif text-3xl font-black text-accent">{value}</div>
          </div>
        ))}
      </div>

      <section className="mt-6 rounded-lg border border-fg/10 bg-fg/[0.04]">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-fg/10 p-4">
          <h2 className="font-serif text-xl font-black text-accent">Medalhas configuradas</h2>
          <div className="flex flex-wrap gap-2">
            {[{ value: 'all', label: 'Todas' }, ...rarities].map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => setFilter(item.value)}
                className={`rounded-full border px-3 py-1 text-xs font-bold ${filter === item.value ? 'border-accent/40 bg-accent/15 text-accent' : 'border-fg/10 bg-black/20 text-fg/50 hover:text-fg'}`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] text-left text-sm">
            <thead className="text-xs uppercase tracking-[0.16em] text-fg/35">
              <tr>
                <th className="px-4 py-3">Medalha</th>
                <th className="px-4 py-3">Categoria</th>
                <th className="px-4 py-3">Raridade</th>
                <th className="px-4 py-3">Requisito</th>
                <th className="px-4 py-3">Recompensas</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {!loading && filteredBadges.length === 0 && (
                <tr><td className="px-4 py-6 text-fg/45" colSpan="7">Nenhuma medalha encontrada.</td></tr>
              )}
              {filteredBadges.map((badge) => (
                <tr key={badge.id} className="align-top">
                  <td className="px-4 py-3">
                    <div className="font-bold text-fg">{badge.name}</div>
                    <div className="mt-1 max-w-md text-xs text-fg/40">{badge.description || 'Sem descrição'}</div>
                  </td>
                  <td className="px-4 py-3 text-fg/60">{badge.category}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full border px-3 py-1 text-xs font-bold ${rarityStyles[badge.rarity] || rarityStyles.common}`}>
                      {rarities.find((item) => item.value === badge.rarity)?.label || badge.rarity}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-fg/60">{getRequirementLabel(badge.requirement)}</td>
                  <td className="px-4 py-3 text-fg/60">{badge.coinReward} moedas<div className="mt-1 text-xs text-fg/35">{badge.xpReward} XP</div></td>
                  <td className="px-4 py-3">
                    <button type="button" onClick={() => toggleActive(badge)} className={`rounded-full border px-3 py-1 text-xs font-bold ${badge.isActive ? 'border-forest-light/30 bg-forest-light/10 text-forest-light' : 'border-fg/15 bg-fg/5 text-fg/45'}`}>
                      {badge.isActive ? 'Ativa' : 'Inativa'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button type="button" onClick={() => editBadge(badge)} className="mr-2 rounded-lg border border-ocean-light/30 px-3 py-1.5 text-xs font-bold text-ocean-light hover:bg-ocean-light/10">Editar</button>
                    <button type="button" onClick={() => removeBadge(badge)} className="rounded-lg border border-energy/30 px-3 py-1.5 text-xs font-bold text-energy hover:bg-energy/10">Remover</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} className="max-w-3xl">
        <div className="rounded-lg border border-accent/25 bg-background p-5 shadow-gold">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-accent/60">Progressão</p>
              <h2 className="font-serif text-2xl font-black text-accent">{editingId ? 'Editar medalha' : 'Nova medalha'}</h2>
            </div>
            <button type="button" onClick={() => setModalOpen(false)} className="rounded-lg border border-fg/10 px-3 py-1.5 text-sm font-bold text-fg/60 hover:bg-fg/10">Fechar</button>
          </div>

          {(error || notice) && (
            <div className={`mt-4 rounded-lg border p-3 text-sm font-bold ${error ? 'border-energy/40 bg-energy/10 text-energy' : 'border-forest-light/40 bg-forest-light/10 text-forest-light'}`}>
              {error || notice}
            </div>
          )}

          <form className="mt-5 grid gap-3" onSubmit={saveBadge}>
            <label className="grid gap-1"><span className={labelClass}>Nome</span><input className={fieldClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></label>
            <label className="grid gap-1"><span className={labelClass}>Descrição</span><textarea className={`${fieldClass} min-h-24 resize-y`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="grid gap-1"><span className={labelClass}>Categoria</span><input className={fieldClass} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></label>
              <label className="grid gap-1"><span className={labelClass}>Raridade</span><select className={fieldClass} value={form.rarity} onChange={(e) => setForm({ ...form, rarity: e.target.value })}>{rarities.map((rarity) => <option key={rarity.value} value={rarity.value}>{rarity.label}</option>)}</select></label>
              <div className="grid gap-2 sm:col-span-2">
                <span className={labelClass}>Imagem URL</span>
                <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                  <input className={fieldClass} value={form.imageUrl} onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} />
                  <button
                    type="button"
                    onClick={generateImage}
                    disabled={generatingImage || saving || form.name.trim().length < 3}
                    className="rounded-lg border border-accent/40 bg-accent/10 px-4 py-2 text-xs font-bold text-accent hover:bg-accent/20 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {generatingImage ? 'A gerar...' : 'Gerar IA'}
                  </button>
                </div>
                {form.imageUrl && (
                  <div className="grid h-24 w-24 place-items-center overflow-hidden rounded-lg border border-accent/30 bg-black/30 p-2">
                    <img src={getImageSrc(form.imageUrl)} alt="" className="h-full w-full object-contain" />
                  </div>
                )}
              </div>
            </div>
            <div className="grid gap-3 rounded-lg border border-fg/10 bg-black/15 p-3 sm:grid-cols-[1fr_150px]">
              <label className="grid gap-1"><span className={labelClass}>Requisito</span><select className={fieldClass} value={form.requirementAction} onChange={(e) => setForm({ ...form, requirementAction: e.target.value })}>{requirementActions.map((action) => <option key={action.value} value={action.value}>{action.label}</option>)}</select></label>
              <label className="grid gap-1"><span className={labelClass}>Meta</span><input className={fieldClass} type="number" min="1" value={form.requirementTarget} onChange={(e) => setForm({ ...form, requirementTarget: e.target.value })} required /></label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="grid gap-1"><span className={labelClass}>Moedas</span><input className={fieldClass} type="number" min="0" value={form.coinReward} onChange={(e) => setForm({ ...form, coinReward: e.target.value })} /></label>
              <label className="grid gap-1"><span className={labelClass}>XP</span><input className={fieldClass} type="number" min="0" value={form.xpReward} onChange={(e) => setForm({ ...form, xpReward: e.target.value })} /></label>
            </div>
            <label className="flex items-center gap-2 rounded-lg border border-fg/10 bg-black/15 p-3 text-sm font-bold text-fg/70"><input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />Ativa</label>
            <button type="submit" disabled={saving} className="rounded-lg bg-accent px-4 py-3 font-serif font-black text-fg shadow-gold disabled:opacity-60">{saving ? 'A guardar...' : editingId ? 'Guardar alterações' : 'Criar medalha'}</button>
          </form>
        </div>
      </Modal>
    </section>
  );
}
